import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import {
    ListActionRow,
    ListEditDeleteActions,
    ListEditIconButton,
    ListPublishButton
} from '../../../components/admin/ListRowActions';
import '../quotes/quote-generator.css';
import './work-order.css';
import WorkOrdersCalendar from './WorkOrdersCalendar';
import { apiRequest } from '../../../api/client';

interface WorkOrderListItem {
    _id: string;
    jobType?: 'inspection' | 'troubleshooting' | 'service_call' | 'other';
    client?: {
        name: string;
        address?: string;
    };
    title: string;
    technicianName?: string;
    status: string;
    schedule?: {
        date?: string;
    };
    createdAt: string;
}

type TechnicianSummary = {
    name: string;
    tasks: number;
    nextTask?: string;
};

function formatJobType(jobType?: string): string {
    if (!jobType) return 'Other';
    if (jobType === 'service_call') return 'Service Call';
    return jobType.charAt(0).toUpperCase() + jobType.slice(1);
}

export default function WorkOrdersList(): React.JSX.Element {
    const navigate = useNavigate();
    const [view, setView] = useState<'list' | 'calendar'>('list');
    const [workOrders, setWorkOrders] = useState<WorkOrderListItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        apiRequest<{ data: WorkOrderListItem[] }>('/api/work-orders')
            .then((data) => {
                setErrorMessage(null);
                setWorkOrders(data.data);
            })
            .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : 'Failed to load jobs.';
                setErrorMessage(message);
                setWorkOrders([]);
            });
    }, []);

    const draftOrders = workOrders.filter((order) => order.status === 'draft');
    const publishedOrders = workOrders.filter((order) => order.status !== 'draft');

    const techniciansThisWeek = useMemo<TechnicianSummary[]>(() => {
        const now = new Date();
        const inSevenDays = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        const summary = new Map<string, TechnicianSummary>();

        publishedOrders.forEach((order) => {
            if (!order.schedule?.date || !order.technicianName) return;
            const date = new Date(order.schedule.date);
            if (Number.isNaN(date.getTime())) return;
            if (date < now || date > inSevenDays) return;

            const existing = summary.get(order.technicianName) || { name: order.technicianName, tasks: 0 };
            existing.tasks += 1;
            if (!existing.nextTask || date < new Date(existing.nextTask)) {
                existing.nextTask = order.schedule.date;
            }
            summary.set(order.technicianName, existing);
        });

        return Array.from(summary.values()).sort((a, b) => (b.tasks - a.tasks));
    }, [publishedOrders]);

    const publishDraft = (draftId: string) => {
        const updated = workOrders.map((order) => {
            if (order._id !== draftId) return order;
            if (!order.schedule?.date) {
                alert('Set a schedule date before publishing.');
                return order;
            }
            return { ...order, status: 'scheduled' };
        });
        apiRequest(`/api/work-orders/${draftId}`, {
            method: 'PATCH',
            json: { status: 'scheduled' }
        })
            .then((data: any) => {
                const updatedOrder = data.data;
                setWorkOrders((prev) => prev.map((order) => (order._id === updatedOrder._id ? updatedOrder : order)));
            })
            .catch(() => {
                setWorkOrders(updated);
            });
    };

    return (
        <AppShell>
            <div className="quote-page">
                <div className="quote-container" style={{ maxWidth: '1000px' }}>
                    {errorMessage && (
                        <div className="banner banner-error" style={{ marginBottom: '16px' }}>
                            {errorMessage}
                        </div>
                    )}
                    <header className="quote-header" style={{ marginBottom: '32px' }}>
                        <div>
                            <h1 style={{ fontSize: '2.5rem', marginBottom: '8px' }}>Jobs</h1>
                            <p style={{ color: 'var(--dash-muted)', margin: 0, fontSize: '1.05rem' }}>
                                Track active jobs and weekly technician assignments.
                            </p>
                        </div>
                        <button
                            className="btn-primary"
                            style={{ padding: '12px 24px', fontSize: '0.95rem', boxShadow: '0 4px 14px rgba(46, 138, 87, 0.3)' }}
                            onClick={() => navigate('/admin/work-order/new')}
                        >
                            + Create New Job
                        </button>
                    </header>

                    <div className="job-view-tabs" role="tablist" aria-label="Jobs view">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === 'list'}
                            className={`job-view-tab${view === 'list' ? ' is-active' : ''}`}
                            onClick={() => setView('list')}
                        >
                            List
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === 'calendar'}
                            className={`job-view-tab${view === 'calendar' ? ' is-active' : ''}`}
                            onClick={() => setView('calendar')}
                        >
                            Calendar
                        </button>
                    </div>

                    {view === 'calendar' ? (
                        <WorkOrdersCalendar />
                    ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                        {draftOrders.length > 0 && (
                        <section>
                            <div className="quote-section-title" style={{ border: 'none', fontSize: '1.25rem', marginBottom: '16px' }}>Drafts ({draftOrders.length})</div>
                            <div className="quote-card" style={{ padding: 0, overflow: 'hidden' }}>
                                    <div className="list-responsive">
                                        <div className="list-table-pane quote-table-container">
                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Scheduled Date</th>
                                                        <th>Client</th>
                                                        <th>Job Title</th>
                                                        <th>Job Type</th>
                                                        <th>Technician</th>
                                                        <th>Status</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {draftOrders.map((order) => (
                                                        <tr key={order._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/work-order/new?id=${order._id}`)}>
                                                            <td>{order.schedule?.date ? new Date(order.schedule.date).toLocaleDateString() : '—'}</td>
                                                            <td>{order.client?.name}</td>
                                                            <td>{order.title}</td>
                                                            <td>{formatJobType(order.jobType)}</td>
                                                            <td>{order.technicianName || 'Unassigned'}</td>
                                                            <td><span className="status-badge draft">Draft</span></td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                <ListActionRow>
                                                                    <ListEditIconButton
                                                                        title="Edit job"
                                                                        onClick={() => navigate(`/admin/work-order/new?id=${order._id}`)}
                                                                    />
                                                                    <ListPublishButton onPublish={() => publishDraft(order._id)} />
                                                                </ListActionRow>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="list-cards-pane">
                                            {draftOrders.map((order) => (
                                                <article
                                                    key={`m-${order._id}`}
                                                    className="list-land-card list-land-card--clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => navigate(`/admin/work-order/new?id=${order._id}`)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            navigate(`/admin/work-order/new?id=${order._id}`);
                                                        }
                                                    }}
                                                >
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Job title</span>
                                                        <span className="list-land-value">{order.title}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Client</span>
                                                        <span className="list-land-value">{order.client?.name || '—'}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Type</span>
                                                        <span className="list-land-value">{formatJobType(order.jobType)}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Scheduled</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {order.schedule?.date ? new Date(order.schedule.date).toLocaleDateString() : '—'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Technician</span>
                                                        <span className="list-land-value">{order.technicianName || 'Unassigned'}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Status</span>
                                                        <span className="list-land-value"><span className="status-badge draft">Draft</span></span>
                                                    </div>
                                                    <div className="list-land-actions">
                                                        <ListActionRow>
                                                            <ListEditIconButton
                                                                title="Edit job"
                                                                onClick={() => navigate(`/admin/work-order/new?id=${order._id}`)}
                                                            />
                                                            <ListPublishButton onPublish={() => publishDraft(order._id)} />
                                                        </ListActionRow>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                            </div>
                        </section>
                        )}

                        <section>
                            <div className="quote-section-title" style={{ border: 'none', fontSize: '1.25rem', marginBottom: '16px' }}>Scheduled Jobs ({publishedOrders.length})</div>
                            <div className="quote-card" style={{ padding: 0, overflow: 'hidden' }}>
                                {publishedOrders.length === 0 ? (
                                    <div className="wo-empty">
                                        <div className="wo-empty-icon">📌</div>
                                        <h3>No Scheduled Jobs</h3>
                                        <p>Publish a job to see it appear here.</p>
                                    </div>
                                ) : (
                                    <div className="list-responsive">
                                        <div className="list-table-pane quote-table-container">
                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Scheduled Date</th>
                                                        <th>Client</th>
                                                        <th>Job Title</th>
                                                        <th>Job Type</th>
                                                        <th>Technician</th>
                                                        <th>Status</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {publishedOrders.map((order) => (
                                                        <tr key={order._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/work-order/new?id=${order._id}`)}>
                                                            <td>{order.schedule?.date ? new Date(order.schedule.date).toLocaleDateString() : '—'}</td>
                                                            <td>{order.client?.name}</td>
                                                            <td>{order.title}</td>
                                                            <td>{formatJobType(order.jobType)}</td>
                                                            <td>{order.technicianName || 'Unassigned'}</td>
                                                            <td><span className="status-badge sent">Scheduled</span></td>
                                                            <td style={{ textAlign: 'right' }}>
                                                                <ListEditDeleteActions
                                                                    onEdit={() => navigate(`/admin/work-order/new?id=${order._id}`)}
                                                                    editTitle="Edit job"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="list-cards-pane">
                                            {publishedOrders.map((order) => (
                                                <article
                                                    key={`m-${order._id}`}
                                                    className="list-land-card list-land-card--clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => navigate(`/admin/work-order/new?id=${order._id}`)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            navigate(`/admin/work-order/new?id=${order._id}`);
                                                        }
                                                    }}
                                                >
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Job title</span>
                                                        <span className="list-land-value">{order.title}</span>
                                                    </div>
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Client</span>
                                                        <span className="list-land-value">{order.client?.name || '—'}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Type</span>
                                                        <span className="list-land-value">{formatJobType(order.jobType)}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Scheduled</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {order.schedule?.date ? new Date(order.schedule.date).toLocaleDateString() : '—'}
                                                        </span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Technician</span>
                                                        <span className="list-land-value">{order.technicianName || 'Unassigned'}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Status</span>
                                                        <span className="list-land-value"><span className="status-badge sent">Scheduled</span></span>
                                                    </div>
                                                    <div className="list-land-actions">
                                                        <ListEditDeleteActions
                                                            onEdit={() => navigate(`/admin/work-order/new?id=${order._id}`)}
                                                            editTitle="Edit job"
                                                        />
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>

                        <section>
                            <div className="quote-section-title" style={{ border: 'none', fontSize: '1.25rem', marginBottom: '16px' }}>Technicians This Week</div>
                            <div className="quote-card" style={{ padding: 0, overflow: 'hidden' }}>
                                {techniciansThisWeek.length === 0 ? (
                                    <div className="wo-empty">
                                        <div className="wo-empty-icon">🧰</div>
                                        <h3>No Assignments Yet</h3>
                                        <p>Assign technicians to scheduled jobs to see weekly coverage.</p>
                                    </div>
                                ) : (
                                    <div className="list-responsive">
                                        <div className="list-table-pane quote-table-container">
                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Technician</th>
                                                        <th>Tasks This Week</th>
                                                        <th>Next Task</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {techniciansThisWeek.map((tech) => (
                                                        <tr key={tech.name}>
                                                            <td>{tech.name}</td>
                                                            <td>{tech.tasks}</td>
                                                            <td>{tech.nextTask ? new Date(tech.nextTask).toLocaleDateString() : '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="list-cards-pane">
                                            {techniciansThisWeek.map((tech) => (
                                                <article key={`m-${tech.name}`} className="list-land-card">
                                                    <div className="list-land-field list-land-field--span">
                                                        <span className="list-land-label">Technician</span>
                                                        <span className="list-land-value">{tech.name}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Tasks this week</span>
                                                        <span className="list-land-value">{tech.tasks}</span>
                                                    </div>
                                                    <div className="list-land-field">
                                                        <span className="list-land-label">Next task</span>
                                                        <span className="list-land-value list-land-value--muted">
                                                            {tech.nextTask ? new Date(tech.nextTask).toLocaleDateString() : '—'}
                                                        </span>
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                    )}

                    <div style={{ marginTop: '40px', textAlign: 'center' }}>
                        <button
                            className="dash-btn dash-btn-ghost"
                            onClick={() => navigate('/admin/dashboard')}
                            style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
